import type { BeckyModel, BeckyRequestPayload } from '@wix/thunderbolt-becky-root'
import {
	deStringifyExperiments,
	getMergedDataWithFlatPageStructure,
	getCompsInFirstFold,
	HEURISTIC_FIRST_FOLD_HEIGHT_DESKTOP,
	HEURISTIC_FIRST_FOLD_HEIGHT_MOBILE,
	geResponsiveCompsInFirstFold,
	getCompsDimensionsHypothesis,
	getRequestedPageCompId,
} from '@wix/thunderbolt-becky-root'
import { BeckyTopology, FetchFn, MetricsReporter } from '@wix/thunderbolt-becky-types'
import { mergeMappers } from '@wix/thunderbolt-components'
import type { Translations } from '@wix/thunderbolt-commons'
import { getComponentMappers } from './componentsSiteAssets'
import { applyAnywhereThemeOverride } from './themeOverrides'
import { getTranslation } from './translationsUrl'

export type ResolveBeckyModelParams = {
	payload: BeckyRequestPayload
	fetchFn: FetchFn
	beckyTopology: BeckyTopology
	shapeStore?: Record<string, string>
	metricsReporter: MetricsReporter
}

export const resolveBeckyModel = async ({
	payload,
	beckyTopology: { publicTopology, environmentTopology },
	fetchFn,
	shapeStore = {},
	metricsReporter,
}: ResolveBeckyModelParams): Promise<BeckyModel> => {
	const {
		pages,
		viewMode,
		beckyExperiments,
		isWixCodeOnPage: isWixCodeOnPageParam,
		isWixCodeOnSite: isWixCodeOnSiteParam,
		hasTPAWorkerOnSite: hasTPAWorkerOnSiteParam,
		freemiumBanner: freemiumBannerParam,
		coBrandingBanner: coBrandingBannerParam,
		dayfulBanner: dayfulBannerParam,
		mobileActionsMenu: mobileActionsMenuParam,
		externalBaseUrl,
		metaSiteId,
		siteId,
		clientSpecMap,
		language,
		originalLanguage,
		isWixSite,
		languageResolutionMethod,
		isMultilingualEnabled,
		useSandboxInHTMLComp,
		routersInfo,
		deviceType,
		qaMode,
		testMode,
		debugMode,
		isInSeo: isInSeoParam,
		isPremiumDomain,
		migratingToOoiWidgetIds,
		stylableItemsCss,
		stylableInstance,
		registryLibrariesTopology,
		isHttps,
		anywhereThemeOverride,
		seoTranslations,
		formFactor,
		disableStaticPagesUrlHierarchy,
		aboveTheFoldSectionsNum,
		isTrackClicksAnalyticsEnabled,
		editorName,
		mappersDenyList,
	} = payload

	const isResponsive = payload.isResponsive === 'true'
	const experiments = deStringifyExperiments(beckyExperiments)

	const [processedPages, componentsMappers] = await Promise.all([
		getMergedDataWithFlatPageStructure(pages, viewMode, isResponsive, experiments),
		getComponentMappers({
			fetchFn,
			registryLibrariesTopology,
			metricsReporter,
			isExperimentOpen: (specName: string) => experiments.hasOwnProperty(specName),
		}),
	])

	const data = anywhereThemeOverride
		? applyAnywhereThemeOverride({ data: processedPages.data, anywhereThemeOverride })
		: processedPages.data

	const isInSeo = isInSeoParam === 'true'

	const siteStructure = { ...processedPages.structure, ...processedPages.masterPageStructure }

	const compsInFoldThreshold =
		!viewMode || viewMode === 'desktop' ? HEURISTIC_FIRST_FOLD_HEIGHT_DESKTOP : HEURISTIC_FIRST_FOLD_HEIGHT_MOBILE
	const compsInFoldMap = isResponsive
		? (experiments['specs.thunderbolt.edixIsInFirstFold'] &&
				geResponsiveCompsInFirstFold(
					processedPages,
					getRequestedPageCompId(pages),
					processedPages.data.layout_data,
					processedPages.data.variants_data,
					aboveTheFoldSectionsNum
				)) ||
		  {}
		: getCompsInFirstFold(siteStructure, compsInFoldThreshold)

	const compsDimensionHypothesis =
		isResponsive && experiments['specs.thunderbolt.shouldUseResponsiveImages']
			? getCompsDimensionsHypothesis(
					siteStructure,
					getRequestedPageCompId(pages),
					processedPages.data.layout_data,
					processedPages.data.variants_data
			  )
			: {}

	const globalTranslations: Translations = await getTranslation(language!)

	const isExperimentOpen = (name: keyof BeckyModel['experiments']) => experiments.hasOwnProperty(name)
	return {
		...processedPages,
		data,
		seoTranslations,
		externalBaseUrl,
		metaSiteId,
		siteId,
		previousStore: new WeakMap(),
		viewMode: viewMode || 'desktop',
		environmentTopology,
		publicTopology,
		language: language!,
		isMultilingualEnabled: isMultilingualEnabled === 'true',
		disableStaticPagesUrlHierarchy: disableStaticPagesUrlHierarchy === 'true',
		languageResolutionMethod,
		originalLanguage,
		useSandboxInHTMLComp: useSandboxInHTMLComp === 'true',
		isResponsive,
		editorName,
		isWixSite: isWixSite === 'true',
		hasTPAWorkerOnSite: hasTPAWorkerOnSiteParam === 'true',
		fetchRequestsStore: {},
		afterFirstRecalculate: false,
		isWixCodeOnPage: isWixCodeOnPageParam === 'true',
		isWixCodeOnSite: isWixCodeOnSiteParam === 'true',
		renderFlags: {
			isWixAdsAllowed: freemiumBannerParam === 'true',
			isWixBlocks: false,
			showScreenInComp: isInSeo,
			componentViewMode: 'preview',
			componentPreviewStates: {},
			isBackToTopButtonAllowed: false,
			shouldBlockGoogleMapInteraction: false,
			shouldResetTinyMenuZIndex: false,
			shouldResetSlideShowNextPrevButtonsPosition: false,
			shouldResetSubscribeFormMinMaxWidth: false,
			shouldUpdateJsonFromMeasureMap: false,
			isPlayingAllowed: false,
			isSocialInteractionAllowed: false,
			isExternalNavigationAllowed: true,
			isSlideShowGalleryClickAllowed: true,
			renderFixedPositionContainers: true,
			shouldResetGalleryToOriginalState: false,
			shouldResetComponent: true,
			siteScale: 1,
			isComponentTransitionAllowed: true,
			allowShowingFixedComponents: true,
			isZoomAllowed: false,
			renderFixedPositionBackgrounds: false,
			extraSiteHeight: 0,
			ignoreComponentsHiddenProperty: [],
			ignoreComponentsCollapsedProperty: [],
			transparentComponentsOverlay: -1,
			showHiddenComponents: false,
			blockingLayer: null,
			allowSiteOverflow: true,
			renderScrollSnap: true,
			renderSticky: true,
			compsToRenderSticky: {},
			enablePageAutoGrowForDetachedComponents: true,
			shouldRenderTPAsIframe: true,
			isAdi: false,
			isLivePreviewOpenForEditor: true,
			isExternalComponentsLoading: false,
		},
		meshRuntimeData: {
			absoluteComps: [],
		},
		shouldRenderWixAdsOnNavigation: freemiumBannerParam === 'true',
		shouldShowCoBrandingBanner: coBrandingBannerParam === 'true',
		shouldShowDayfulBanner: dayfulBannerParam === 'true',
		shouldShowMobileActionsMenu: mobileActionsMenuParam === 'true',
		isPremiumDomain: isPremiumDomain === 'true',
		clientSpecMap,
		componentsMappers: mergeMappers(componentsMappers, isExperimentOpen),
		experiments,
		routersInfo: routersInfo!,
		deviceType: deviceType!,
		qaMode: qaMode === 'true',
		testMode: testMode === 'true',
		debugMode: debugMode === 'true',
		isInSeo,
		migratingToOoiWidgetIds: migratingToOoiWidgetIds ? migratingToOoiWidgetIds.split(',') : [],
		stylableItemsCss: stylableItemsCss || {},
		stylableInstance,
		themeCounter: 0,
		enableVariantsTransitionsInEditor: true,
		compIdToIgnoredTransformProps: {},
		ignoredTransformPropsForType: {},
		globalIgnoredTransformProps: [],
		isHttps: isHttps === 'true',
		shapeStore,
		compsInFoldMap,
		compsDimensionHypothesis,
		imageCompsTargetScale: 1,
		formFactor,
		anywhereThemeOverride,
		activeVariant: '',
		activeBreakpoints: {},
		globalTranslations,
		ooiCssConfigStore: {},
		ooiWidgetsAppSettings: {},
		isTrackClicksAnalyticsEnabled: isTrackClicksAnalyticsEnabled === 'true',
		mappersDenyList: mappersDenyList ? mappersDenyList.split(',') : [],
		isOneDocMigrated: !!siteStructure.masterPage.layoutQuery,
	}
}
